var React = require('react');

/**
 * This function takes in a minimum value and a maximum value and returns an array of five equally spaced values between them.
 * @param {number} min - The minimum value.
 * @param {number} max - The maximum value.
 * @returns {Array} The array of equally spaced values.
 */
export const getEqualDistanceValues = (min, max) => {
  // Assign 0 to the minimum value if it is less than 0
  if (min < 0) {
    min = 0;
  }

  // Calculate the interval between each value
  const interval = (max - min) / 4;

  // Create an array to hold the values, starting with the minimum value
  const values = [min.toFixed(2)];

  // Calculate the remaining three values and add them to the array
  for (let i = 1; i <= 3; i++) {
    // Calculate the value as the minimum value plus the interval times the index
    const value = min + interval * i;

    // Add the value to the array, rounded to one decimal places
    values.push(value.toFixed(2));
  }

  // Add the maximum value to the array and return it
  values.push(max.toFixed(2));
  return values;
};

/**
 * Farming-unit and field NDVI images come from the same satellite pass but are
 * sometimes stored with capture timestamps that differ by a few minutes (e.g.
 * a farming-unit image at 18:05:01 and its field images at 18:15:14 on the same
 * day). Field tiles are keyed by the field timestamp, so an exact-timestamp
 * join against the farming-unit values drops the farming-unit image on those
 * dates and the map renders nothing in farming-unit level view. Matching by
 * UTC calendar day recovers it.
 */
const toUtcDayKey = unixSeconds => {
  const date = new Date(Number(unixSeconds) * 1000);
  return `${date.getUTCFullYear()}-${date.getUTCMonth()}-${date.getUTCDate()}`;
};

/**
 * Public UTC-day key for an NDVI capture timestamp. Use to bucket field tiles
 * by calendar day (e.g. dedupe the timeline to one row per day).
 */
export const getNdviDayKey = timestamp => toUtcDayKey(timestamp);

/**
 * Build a { dayKey: ndviValue } index from a farming unit's NDVI values so
 * field tiles can be matched to the farming-unit image by calendar day.
 */
export const buildFarmingUnitNdviDayIndex = farmingUnitValues => {
  const index = {};
  if (!farmingUnitValues) {
    return index;
  }
  for (const key in farmingUnitValues) {
    index[toUtcDayKey(key)] = farmingUnitValues[key];
  }
  return index;
};

/**
 * Resolve the farming-unit NDVI image for a field tile timestamp: exact
 * timestamp match first, then the farming-unit image captured on the same UTC
 * day. `dayIndex` is the output of buildFarmingUnitNdviDayIndex.
 */
export const getFarmingUnitNdviForTimestamp = (
  farmingUnitValues,
  dayIndex,
  timestamp
) => {
  if (farmingUnitValues && farmingUnitValues[timestamp]) {
    return farmingUnitValues[timestamp];
  }
  if (dayIndex) {
    return dayIndex[toUtcDayKey(timestamp)];
  }
  return undefined;
};

/**
 * Collapse an NDVI tiles map keyed by exact capture timestamp into one entry
 * per UTC day. Different fields of a farming unit are captured seconds apart in
 * the same satellite pass, so keying the timeline by exact timestamp produces
 * duplicate rows for one calendar day and rows whose timestamp happens to miss
 * the selected field render no image. Merging same-day tiles under the day's
 * latest timestamp yields one timeline row per day that holds every field tile
 * (and the farming-unit image) captured that day.
 */
export const collapseNdviTilesByDay = tilesByTimestamp => {
  const collapsed = {};
  if (!tilesByTimestamp) {
    return collapsed;
  }
  const dayToTimestamps = {};
  for (const timestamp in tilesByTimestamp) {
    const dayKey = toUtcDayKey(timestamp);
    (dayToTimestamps[dayKey] = dayToTimestamps[dayKey] || []).push(timestamp);
  }
  for (const dayKey in dayToTimestamps) {
    const timestamps = dayToTimestamps[dayKey];
    // Latest timestamp of the day is the stable canonical key and still renders
    // the correct date via dayjs.unix().
    const canonical = timestamps.reduce((latest, ts) =>
      Number(ts) > Number(latest) ? ts : latest,
    );
    const mergedTiles = [];
    for (const ts of timestamps) {
      mergedTiles.push(...tilesByTimestamp[ts]);
    }
    collapsed[canonical] = mergedTiles;
  }
  return collapsed;
};
